import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbCalendar, NgbDateParserFormatter, NgbDate, NgbTimeStruct, NgbDateStruct, NgbModalRef, NgbModalOptions, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ErrorStrings, ValidationPatterns } from 'src/app/constants/form.constants';
import { PetService } from 'src/app/services/pet/pet.service';
import { ProductType, User, UserAddress, UserAddressType, UserTelephone, UserTelephoneType } from 'src/app/interfaces/user';
import { Pet, PetGender, PetMissingReport, PetMissingReportStatus, PetMissingReportType } from 'src/app/interfaces/pet';
import { Alert } from 'src/app/interfaces/alert';
import { NgForm } from '@angular/forms';
import { CommonUtilService } from 'src/app/services/common/common-util.service';
import { MapSearchModalComponent } from 'src/app/modals/map-search-modal/map-search-modal.component';
import { ImageUploaderModalComponent, ImageContent } from 'src/app/modals/image-uploader-modal/image-uploader-modal.component';
import { UserService } from 'src/app/services/user/user.service';
import { CloudinaryService } from 'src/app/services/cloudinary/cloudinary.service';

@Component({
  selector: 'app-report-missing',
  templateUrl: './report-missing.component.html',
  styleUrls: ['./report-missing.component.scss']
})
export class ReportMissingPetComponent implements OnInit, OnDestroy {
  errorStrings = ErrorStrings;
  loading = false;
  alert: Alert = null;
  lastSeenDateValue: NgbDateStruct;
  lastSeenTimeValue: NgbTimeStruct;
  pet: Pet;
  today: NgbDate;
  minDate: NgbDateStruct;
  user: User;
  validationPatterns = ValidationPatterns;
  petGender = PetGender;
  mainAddress: UserAddress;
  telephones: { mainPhoneNumber: string; secondaryPhoneNumber: string };
  currentUrl: string;
  mapSearchModalRef?: NgbModalRef;
  imageUploaderRef?: NgbModalRef;
  petImage: string;
  uploadedImageId?: string;
  uploadedImageUrl?: string;
  useDefaultPicture = false;
  editPetMissingReportId: number;
  report: PetMissingReport;
  reports: PetMissingReport[];
  public position = {
    lat: 54.11815748515591,
    lng: -2.541813085952347
  }
  mapOptions: google.maps.MapOptions = {
    center: {
      lat: 54.11815748515591,
      lng: -2.541813085952347
    },
    zoom: 5,
    disableDefaultUI: true,
    scrollwheel: false,
    draggable: false,
    clickableIcons: false,
    gestureHandling: 'auto'
  };
  
  circleOptions: google.maps.CircleOptions = {
    fillColor: '#ee7203',
    strokeColor: '#ee7203',
    strokeWeight: 1,
    clickable: false
  };

  petMissingReportType = PetMissingReportType;
  productType = ProductType;

  constructor(
    private route: ActivatedRoute,
    private calendar: NgbCalendar,
    private petService: PetService,
    public formatter: NgbDateParserFormatter,
    private commonUtilService: CommonUtilService,
    private router: Router,
    private modalService: NgbModal,
    public userService: UserService,
    private cdr: ChangeDetectorRef,
    private cloudinaryService: CloudinaryService
    ) {
    this.user = this.route.snapshot.data.user as User;
    this.pet = this.route.snapshot.data.pet as Pet;
    this.reports = this.route.snapshot.data.reports as PetMissingReport[];

    const reference = this.route.snapshot.paramMap.get('reference');
    this.editPetMissingReportId = reference ? parseInt(reference, 10) : null;
    this.today = this.calendar.getToday();
    this.minDate = {
      day: 1,
      month: 1,
      year: this.today.year - 99
    };
  }

  submit(form: NgForm) {
    this.report.lastSeen.date = this.commonUtilService.createDateObject(this.lastSeenDateValue, this.lastSeenTimeValue);
    this.closeAlert();
    this.loading = true;
    if (this.editPetMissingReportId) {
      this.petService.reportMissingUpdate(this.report, this.editPetMissingReportId)
      .subscribe(
        report => {
          this.handleSuccessReportSubmission(report, form);
        },
        err => {
          this.handleErrorReportSubmission();
        }
      )
      .add(() => {
        this.loading = false;
      });
    } else {
      this.petService.reportMissing(this.report, this.pet.id)
        .subscribe(
          report => {
            this.handleSuccessReportSubmission(report, form);
          },
          err => {
            this.handleErrorReportSubmission();
          }
        )
        .add(() => {
          this.loading = false;
        });
    }

  }

  closeAlert() {
    this.alert = null;
  }

  openMap(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'lg'
    };
  
    this.mapSearchModalRef = this.modalService.open(MapSearchModalComponent, modalOptions);
    this.mapSearchModalRef.componentInstance.title = `Search last seen location`;
    this.mapSearchModalRef.componentInstance.latitude = this.report.lastSeen.location.latitude ? this.report.lastSeen.location.latitude : 54.11815748515591;
    this.mapSearchModalRef.componentInstance.longitude = this.report.lastSeen.location.longitude ? this.report.lastSeen.location.longitude : -2.541813085952347;
  
    this.mapSearchModalRef.result
      .then((res: { location: { latitude: number; longitude: number }; address: string }) => {
        if (res && res.location) {
            this.report.lastSeen.location.latitude = res.location.latitude;
            this.report.lastSeen.location.longitude = res.location.longitude;
            this.position.lat = res.location.latitude;
            this.position.lng = res.location.longitude;
            this.cdr.detectChanges();
        }
      })
      .catch(() => {});
  }

  public getPetImageUrl(petImageId: string): string {
    return this.cloudinaryService.getImageUrl(petImageId, 360, 360, null);
  }

  openImageUploader(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'md'
    };

    this.imageUploaderRef = this.modalService.open(ImageUploaderModalComponent, modalOptions);
    this.imageUploaderRef.componentInstance.title = 'Add an image';
    this.imageUploaderRef.componentInstance.pet = this.pet;
    if (this.useDefaultPicture) {
      this.imageUploaderRef.componentInstance.currentImageUrl = this.pet.imageUrl;
      this.imageUploaderRef.componentInstance.currentImageId = this.pet.imageId;
    } else {
      this.imageUploaderRef.componentInstance.currentImageUrl = this.uploadedImageUrl;
      this.imageUploaderRef.componentInstance.currentImageId = this.uploadedImageId;
      this.imageUploaderRef.componentInstance.disablePetDefault = true;
      this.imageUploaderRef.componentInstance.filters = ['lostpet'];
    }

    this.imageUploaderRef.result
      .then(res => {
        const imageContent = res as ImageContent;
        this.updateImage(imageContent);
      })
      .catch(() => { });
  }

  togglePictureType(): void {
    this.useDefaultPicture = !this.useDefaultPicture;
    if (this.useDefaultPicture) {
      this.report.lastSeen.imageId = this.pet.imageId;
      this.report.lastSeen.imageUrl = this.pet.imageUrl;
    } else {
      this.report.lastSeen.imageId = this.uploadedImageId;
      this.report.lastSeen.imageUrl = this.uploadedImageUrl;
    }
    this.petImage = this.getPetImageUrl(this.report.lastSeen.imageId);
  }

  updateImage(imageContent: ImageContent): void {
    if (this.useDefaultPicture) {
      this.pet.imageId = imageContent.fileName;
      this.pet.imageUrl = imageContent.filePath;
    } else {
      this.uploadedImageId = imageContent.fileName;
      this.uploadedImageUrl = imageContent.filePath;
    }
    this.report.lastSeen.imageId = imageContent.fileName;
    this.report.lastSeen.imageUrl = imageContent.filePath;
    this.petImage = this.getPetImageUrl(imageContent.fileName);
  }

  private getMainAddress(user: User): UserAddress {
    return user.addresses.filter(address => address.addressType === UserAddressType.Main)[0];
  }

  private getTelephones(user: User): { mainPhoneNumber: string; secondaryPhoneNumber: string } {
    return {
      mainPhoneNumber: user.contact.mainPhoneNumber || '',
      secondaryPhoneNumber: user.contact.secondaryPhoneNumber || '',
    }
  }

  private getPetMissingReport(id: number): PetMissingReport {
    if (id) {
      const report = this.pet.missingPetReports.find(r => r.id === id);
      return {
        lastSeen: {
          date: report.lastSeen.date,
          notes: report.lastSeen.notes,
          location: {
            latitude: report.lastSeen.location.latitude,
            longitude: report.lastSeen.location.longitude,
          },
          imageId: report.lastSeen.imageId,
          imageUrl: report.lastSeen.imageUrl,
        },
        reportType: report.reportType
      };
    }

    const draftReport = this.reports.find(
      (r) =>
        r.reportStatus !== PetMissingReportStatus.AwaitingModeration &&
        r.reportStatus !== PetMissingReportStatus.Open &&
        r.reportStatus !== PetMissingReportStatus.AwaitingClosureFound &&
        r.reportStatus !== PetMissingReportStatus.AwaitingClosureDeceased
    );

    return {
      lastSeen: {
        date: null,
        notes: null,
        location: {
          latitude: null,
          longitude: null,
        },
        imageUrl: null,
        imageId: null,
      },
      reportType: draftReport?.reportType ?? PetMissingReportType.Missing
    };
  }

  private setInitialLastSeenDate(): void {
    if (this.report.lastSeen.date) {
      this.lastSeenDateValue = {
        day: this.report.lastSeen.date.getDate(),
        month: this.report.lastSeen.date.getMonth() + 1,
        year: this.report.lastSeen.date.getFullYear()
      };
      this.lastSeenTimeValue = {
        hour: this.report.lastSeen.date.getHours(),
        minute: this.report.lastSeen.date.getMinutes(),
        second: 0
      };
    }
  }

  private setInitialPetImage(): void {
    // If a report imageId exists it's an Edit report type
    if (this.report.lastSeen.imageId) {
      // Checking if the image chosen for the existing report is the default for the pet or a different one
      if (this.report.lastSeen.imageId === this.pet.imageId) {
        this.petImage = this.getPetImageUrl(this.pet.imageId);
        this.useDefaultPicture = true;
      } else {
        this.uploadedImageId = this.report.lastSeen.imageId;
        this.uploadedImageUrl = this.report.lastSeen.imageUrl;
        this.petImage = this.getPetImageUrl(this.report.lastSeen.imageId);
        this.useDefaultPicture = false;
      }
    } else {
      // It's a new report
      if (this.pet.imageId) {
        this.report.lastSeen.imageId = this.pet.imageId;
        this.report.lastSeen.imageUrl = this.pet.imageUrl;
        this.petImage = this.getPetImageUrl(this.pet.imageId);
        this.useDefaultPicture = true;
      } else {
        this.useDefaultPicture = false;
      }
    }
  }

  private handleSuccessReportSubmission(report: PetMissingReport, form: NgForm): void {
    this.alert = {
      type: 'success',
      message: 'Your missing pet report has been successfully submitted.'
    };
    form.resetForm();
    this.router.navigate(['/lost-and-found', 'missing-pet', report.id]);
  }

  private handleErrorReportSubmission(): void {
    this.alert = {
      type: 'danger',
      message: 'An error has occurred. Please try again later.'
    };
  }

  setInitialReportType() {
    // Set missing type as default
    this.report.reportType = this.petMissingReportType.Missing;
  }

  toggleReportType(event) {
    const radioValue = +event.target.value;
    if (radioValue === this.petMissingReportType.Missing || radioValue === this.petMissingReportType.Stolen) {
      this.report.reportType = radioValue;
    }
  }

  ngOnInit(): void {
    if (this.pet.name === '[to_be_completed_by_keeper]') {
      this.router.navigate(['/my-pet', this.pet.id, 'confirm']);
    } else {
      this.mainAddress = this.getMainAddress(this.user);
      this.telephones = this.getTelephones(this.user);
      this.currentUrl = this.router.routerState.snapshot.url;

      // This is required for the 'Edit' functionality
      this.report = this.getPetMissingReport(this.editPetMissingReportId);
      this.setInitialLastSeenDate();
      this.setInitialPetImage();

      this.setInitialReportType();
    }
  }

  ngOnDestroy(): void {
    this.mapSearchModalRef?.dismiss('destroyed');
    this.imageUploaderRef?.dismiss('destroyed');
  }
}
